import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Direct Competitor", "Aspirational Competitor", "Price Competitor", "Marketplace Leader", "Brand Keyword Competitor"];
const EMPTY = { competitor_name: "", competitor_type: "Direct Competitor", category: "", subcategory: "", notes: "" };

export default function Competitors() {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    if (!selectedBrandId) return setItems([]);
    api.get(`/competitors?brand_id=${selectedBrandId}`).then((r) => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [selectedBrandId]);

  const save = async () => {
    if (!selectedBrandId) return toast.error("Select a brand first");
    try {
      const payload = { ...form, brand_id: selectedBrandId };
      if (editing) await api.put(`/competitors/${editing.id}`, payload);
      else await api.post("/competitors", payload);
      toast.success(editing ? "Competitor updated" : "Competitor created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Competitors</h1>
          <p className="mt-1 text-sm text-slate-500">Track direct, aspirational, price and brand competitors.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="competitor-add-btn" disabled={!selectedBrandId} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> New competitor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit competitor" : "Create competitor"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <Field label="Name" required><Input data-testid="competitor-form-name" value={form.competitor_name} onChange={(e) => setForm({ ...form, competitor_name: e.target.value })} /></Field>
              <Field label="Type">
                <Select value={form.competitor_type} onValueChange={(v) => setForm({ ...form, competitor_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Category"><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
              <Field label="Subcategory"><Input value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></Field>
              <Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            <DialogFooter><Button onClick={save} data-testid="competitor-form-save" className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="competitors-list">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold tracking-tight text-slate-900">{c.competitor_name}</h3>
                <span className="mt-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{c.competitor_type}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            {c.notes ? <p className="mt-3 text-sm text-slate-600 line-clamp-3">{c.notes}</p> : null}
          </div>
        ))}
        {!items.length && <div className="text-sm text-slate-500">{selectedBrandId ? "No competitors yet." : "Select a brand."}</div>}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
